import React, { useCallback, useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { Modal, Spinner, Table } from "react-bootstrap";
import { Paper } from "@material-ui/core";
import SVG from "react-inlinesvg";
import { baseUrl } from "../../../services/config";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { PremissionChecker } from "../../../services/PremissionChecker";
import { useSelector } from "react-redux";

function DefineCRMInquiryStatuses() {
  const alert = useAlert();
  const user = useSelector((state) => state.user.user);
  const authtoken = localStorage.getItem("userToken");
  const [inquiryStatuses, setInquiryStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState(null);
  const [name, setName] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const showErrors = useCallback(
    (data, fallback) => {
      const message = data?.metadata?.message;
      if (message && typeof message === "object") {
        Object.values(message).forEach((item) => alert.error(item));
      } else {
        alert.error(message || fallback);
      }
    },
    [alert],
  );

  const getInquiryStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/crm-inquiry-statuses`, {
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to load CRM inquiry statuses");
        return;
      }
      setInquiryStatuses(data?.payload || []);
    } catch (error) {
      alert.error("Unable to load CRM inquiry statuses");
    } finally {
      setLoading(false);
    }
  }, [alert, authtoken, showErrors]);

  useEffect(() => {
    getInquiryStatuses();
  }, [getInquiryStatuses]);

  const closeModal = () => {
    setModal(null);
    setName("");
    setSelectedId(null);
  };

  const saveInquiryStatuses = async () => {
    if (!name.trim()) {
      alert.error("Inquiry Statuses Name Is Required");
      return;
    }

    const isEditing = modal === "edit";
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-inquiry-statuses${isEditing ? `/${selectedId}` : ""}`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`,
          },
          body: JSON.stringify({ name: name.trim() }),
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(
          data,
          `Unable to ${isEditing ? "update" : "add"} inquiry status`,
        );
        return;
      }
      alert.success(
        `Inquiry status successfully ${isEditing ? "updated" : "added"}`,
      );
      closeModal();
      getInquiryStatuses();
    } catch (error) {
      alert.error(`Unable to ${isEditing ? "update" : "add"} inquiry status`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteInquiryStatus = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-inquiry-statuses/${selectedId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authtoken}` },
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to delete inquiry status");
        return;
      }
      alert.success(data?.payload || "Inquiry status successfully deleted");
      closeModal();
      getInquiryStatuses();
    } catch (error) {
      alert.error("Unable to delete inquiry status");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      {PremissionChecker(user, "crm_inquiry_status.create") && (
        <button
          className="btn btn-primary mr-2"
          onClick={() => setModal("add")}
        >
          Add CRM Inquiry Status
        </button>
      )}

      <Table className="mt-5">
        <thead>
          <tr className="text-center">
            <th>ID</th>
            <th>Inquiry Status Name</th>
            {PremissionChecker(user, "crm_inquiry_status.update") && (
              <th>Update</th>
            )}
            {PremissionChecker(user, "crm_inquiry_status.delete") && (
              <th>Delete</th>
            )}
          </tr>
        </thead>
        <tbody>
          {inquiryStatuses.map((inquiryStatus) => (
            <tr className="text-center" key={inquiryStatus.id}>
              <td>{inquiryStatus.id}</td>
              <td>{inquiryStatus.name}</td>
              {PremissionChecker(user, "crm_inquiry_status.update") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-3"
                    onClick={() => {
                      setSelectedId(inquiryStatus.id);
                      setName(inquiryStatus.name || "");
                      setModal("edit");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Write.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
              {PremissionChecker(user, "crm_inquiry_status.delete") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-danger btn-sm mx-3"
                    onClick={() => {
                      setSelectedId(inquiryStatus.id);
                      setModal("delete");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-danger">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Delete-user.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {loading && (
        <div className="row justify-content-center">
          <Spinner animation="border" />
        </div>
      )}

      <Modal
        show={modal === "add" || modal === "edit"}
        onHide={closeModal}
        size="md"
      >
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            {modal === "edit" ? "Update" : "Add New"} CRM Inquiry Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-control-label">
            <b>
              Enter Inquiry Status Name <span style={{ color: "red" }}>*</span>
            </b>
          </label>
          <input
            className="form-control"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Enter Inquiry Status Name"
          />
        </Modal.Body>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button
              className="btn btn-primary w-100px"
              onClick={saveInquiryStatuses}
            >
              {modal === "edit" ? "Update" : "Add"}
            </button>
          )}
          <button className="btn btn-danger" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={modal === "delete"} onHide={closeModal}>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Are you sure you want to delete this CRM inquiry status?
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn button_blue" onClick={deleteInquiryStatus}>
              Delete
            </button>
          )}
          <button className="btn btn-danger btn_width" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
}

export default DefineCRMInquiryStatuses;
